import "./panel.css";
import type { Section } from "../sections";

function CirclesFigure() {
  return (
    <svg viewBox="0 0 320 200" width="280" height="175" aria-hidden="true" style={{ display: "block" }}>
      <circle cx="60" cy="100" r="48" fill="var(--ink)" />
      <text x="60" y="176" textAnchor="middle" fill="currentColor"
        style={{ fontFamily: "var(--ds-font-mono)", fontSize: 9, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase" }}>
        REAL INBOX
      </text>
      <line x1="116" y1="100" x2="204" y2="100" stroke="var(--ink)" strokeWidth="1" />
      <polygon points="204,100 196,96 196,104" fill="var(--ink)" />
      <circle cx="260" cy="100" r="48" fill="none" stroke="var(--action)" strokeWidth="2" />
      <circle cx="260" cy="100" r="6" fill="var(--action)" />
      <text x="260" y="176" textAnchor="middle" fill="var(--action)"
        style={{ fontFamily: "var(--ds-font-mono)", fontSize: 9, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase" }}>
        DUSTO
      </text>
    </svg>
  );
}

function PhilosophyFigure() {
  return (
    <svg viewBox="0 0 320 200" width="300" height="187" aria-hidden="true" style={{ display: "block" }}>
      {/* Outer square — 100×100, centered at 160,100 */}
      <rect x="110" y="50" width="100" height="100" fill="none" stroke="var(--ink)" strokeWidth="1.5" />
      {/* Middle diamond — inscribed in outer square */}
      <polygon points="160,50 210,100 160,150 110,100" fill="none" stroke="var(--ink)" strokeWidth="1" strokeOpacity="0.4" />
      {/* Inner square — inscribed in diamond, 50×50 */}
      <rect x="135" y="75" width="50" height="50" fill="none" stroke="var(--action)" strokeWidth="1.5" />
      {/* Center point */}
      <circle cx="160" cy="100" r="3.5" fill="var(--ink)" />
    </svg>
  );
}

function ContactRingsFigure() {
  return (
    <svg viewBox="0 0 320 200" width="280" height="175" aria-hidden="true" style={{ display: "block" }}>
      <circle cx="160" cy="100" r="52" fill="none" stroke="var(--ink)" strokeWidth="1" strokeOpacity="0.25" strokeDasharray="3 4" />
      <circle cx="160" cy="100" r="28" fill="none" stroke="var(--ink)" strokeWidth="1.5" />
      <circle cx="160" cy="100" r="6" fill="var(--ink)" />
    </svg>
  );
}

export function Panel({ section, onEnterBlog }: { section: Section; onEnterBlog?: () => void }) {
  return (
    <div className="panel">
      {section.id === "spicadust" ? (
        <h1 className="panel__title panel__title--wordmark">spicadust_</h1>
      ) : (
        <h1
          className={
            "panel__title" + (section.titleMono ? " panel__title--mono" : "")
          }
        >
          {section.title}
        </h1>
      )}

      <div className="panel__center">
        {(section.art === "photo" || section.art === "photo-hero") && section.photoSrc ? (
          <figure className="panel__figure">
            <img src={section.photoSrc} className={section.art === "photo-hero" ? "panel__photo panel__photo--hero" : "panel__photo"} alt="" aria-hidden="true" />
            {section.photoCaption && (
              <figcaption className="panel__caption">
                {section.photoCaption.before}
                <a
                  className="panel__caption-link"
                  href={section.photoCaption.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {section.photoCaption.label}
                </a>
                {section.photoCaption.after}
              </figcaption>
            )}
          </figure>
        ) : section.art === "circles" ? (
          <CirclesFigure />
        ) : section.art === "grid-not-chat" ? (
          <PhilosophyFigure />
        ) : section.art === "contact-rings" ? (
          <ContactRingsFigure />
        ) : (
          <div className="panel__swatches" aria-hidden="true">
            {section.swatches.map((color, i) => (
              <div
                key={i}
                className="panel__swatch"
                style={{ background: color }}
              />
            ))}
          </div>
        )}

        {section.spec && (
          <>
            <div className="panel__rule" />
            <div className="panel__spec">
              <div className="panel__spec-label">{section.spec.label}</div>
              {section.spec.rows.map((row) => (
                <div key={row.key} className="panel__spec-row">
                  <span className="key">{row.key}</span>
                  <span className="dots" />
                  <span className="value">{row.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="panel__body">
        {section.body.map((node, i) => {
          if (node.kind === "p") return <p key={i}>{node.text}</p>;
          if (node.kind === "p-link") {
            return (
              <p key={i}>
                {node.before}
                <a
                  className="panel__link"
                  href={node.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {node.label}
                </a>
                {node.after}
              </p>
            );
          }
          if (node.kind === "p-icon-link") {
            return (
              <p key={i}>
                {node.before}
                <a
                  className="panel__icon-link"
                  href={node.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={node.icon === "x" ? "X" : node.icon}
                >
                  {node.icon === "x" && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  )}
                </a>
                {node.after}
              </p>
            );
          }
          if (node.kind === "mailto") {
            return (
              <a
                key={i}
                className="panel__mailto"
                href={`mailto:${node.address}`}
              >
                {node.address}
              </a>
            );
          }
          return (
            <a
              key={i}
              className="panel__mailto"
              href={node.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {node.label}
            </a>
          );
        })}
        {section.id === "philosophy" && onEnterBlog && (
          <p>
            <button
              type="button"
              className="panel__link panel__link--button"
              onClick={onEnterBlog}
            >
              read posts -&gt;
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
